import type { interfaces } from '@patsnap/tahiti-core/shared/inversify';
import { FtoReportTaskStatusServiceImpl } from './services/impls/task-status-service';
import { FtoReportTaskStatusService } from './services/task-status-service';
import { FtoReportInputLanguageDetectionService } from './services/input-language-detection-service';
import { FtoServiceInputLanguageDetectionServiceImpl } from './services/impls/input-language-detection-service';
import { FtoReportTaskCreator } from './services/task-creator';
import { FtoReportTaskCreatorImpl } from './services/impls/task-creator';
import { FtoReportFeatureIdentify } from './services/feature-identify';
import { FtoReportFeatureIdentifyImpl } from './services/impls/feature-identify';
import { FtoReportComplexRetrievalRepository, FtoReportComplexRetrievalService } from './services/complex-retrieval-service';
import { FtoReportComplexRetrievalServiceImpl } from './services/impls/complex-retrieval-service';
import { FtoReportRetrievalProgressModelImpl } from './models/impls/retrieval-progress-model';
import { FtoReportRetrievalProgressModel } from './models/retrieval-progress-model';
import { FtoReportRetrievalProgressServiceImpl } from './services/impls/retrieval-progress-service';
import { FtoReportRetrievalProgressService } from './services/retrieval-progress-service';
import { FtoReportCurrentMatchedPatentModel } from './models/current-matched-patent-model';
import { FtoReportCurrentMatchedPatentModelImpl } from './models/impls/current-matched-patent-model';
import { FtoReportComplexRetrievalRepositoryImpl } from './repositories/impls/complex-retrieval-repository';
import { FtoReportTaskStatusModelImpl } from './models/impls/task-status-model';
import { FtoReportTaskStatusModel } from './models/task-status-model';
import { FtoReportRetrievalResultReportModelImpl } from './models/impls/retrieval-result-report-model';
import { FtoReportRetrievalResultReportModel } from './models/retrieval-result-report-model';
import { FtoReportRetrievalResultReportServiceImpl } from './services/impls/retrieval-result-report-service';
import { FtoReportComparativeLiteraturePublicityService, FtoReportTitleService, FtoReportNoveltyReviewService, FtoReportOriginalInputService, FtoReportRetrievalResultReportSubtitleInfoService, FtoReportTechEssentialService, FtoReportTechFeaturesService } from './services/retrieval-result-report-service';
import { FtoReportAdjustTechFeaturesServiceImpl } from './services/impls/adjust-tech-features-service';
import { FtoReportAdjustTechFeaturesService } from './services/adjust-tech-features-service';

// 导入状态相关类
import { FtoReportStateService } from './services/state-service';
import { FtoReportStateServiceImpl } from './services/impls/state-service-impl';
import { FtoReportSearchFinishedState } from './services/states/impls/search-finished-state';
import { FtoReportInitialState } from './services/states/impls/initial-state';
import { FtoReportContextImpl } from './services/states/impls/fto-report-context';
import { FtoReportResultState } from './services/states/impls/result-state';
import { FtoReportTechFeaturePullSuccessState } from './services/states/impls/tech-feature-pull-success-state';
import { FtoReportContext } from './services/states/novelty-check-report-context';
import { FtoReportSearchInputState } from './services/states/impls/search-input-state';
import { FtoReportSimilarServiceImpl } from './services/impls/similar-service-impl';
import { FtoReportSimilarService } from './services/similar-service';
import { FtoReportSimilarRepositoryImpl } from './repositories/impls/similar-repository-impl';
import { FtoReportSimilarRepository } from './repositories/similar-repository';
import { FtoReportTechFeaturePullStartState } from './services/states/impls/tech-feature-pull-start-state';
import { CommandContribution } from '@patsnap/tahiti-core';
import { FtoReportCommandContribution } from './services/impls/command-contribution';
import { FtoReportSummaryTechFeaturesServiceImpl } from './services/impls/summary-tech-features-service';
import { FtoReportSummaryTechFeaturesService } from './services/summary-tech-features-service';
import { FtoReportSummaryStartState } from './services/states/impls/summary-start-state';
import { FtoReportSummaryEndState } from './services/states/impls/summary-end-state';
import { FtoReportResultRefreshImpl } from './services/impls/report-result-refresh';
import { FtoReportResultRefresh } from './services/report-result-refresh';
import { FtoReportNoveltyFeatureControlServiceImpl } from './services/impls/feature-control-service-impl';
import { FtoReportNoveltyFeatureControlService } from './services/feature-control-service';
import { FtoReportApiServiceImpl } from './apis/impls/fto-report-api-service';
import { FtoReportCreateTaskApi } from './apis/create-novelty-check-task-api';
import { FtoReportFeatureRecognizeApi } from './apis/feature-recognize-api';
import { FtoReportConclusionApi } from './apis/comparison-public-situation-api';
import { FtoReportTitleApi } from './apis/novelty-check-report-title-api';
import { FtoReportFeatureComparisonApi } from './apis/novelty-feature-comparison-api';
import { FtoReportInputLanguageDetectApi } from './apis/novelty-input-language-detect-api';
import { FtoReportInputSummaryApi } from './apis/novelty-input-summary-api';
import { FtoReportSubtitleInfoApi } from './apis/novelty-report-subtitle-info-api';
import { FtoReportSearchApi } from './apis/search-novelty-api';
import { FtoReportResultSearchApi } from './apis/search-novelty-result-api';
import { FtoReportTechEssentialUpdateApi } from './apis/tech-essential-update-api';
import { FtoReportTechFeatureSubmitApi } from './apis/tech-feature-submit-api';
import { FtoReportTechnicalFeaturesGetApi } from './apis/technical-features-get-api';
import { FtoReportTerminateSearchAgentApi } from './apis/terminate-search-agent-api';
import { FtoReportUpdateSimilarListApi } from './apis/update-novelty-similar-list-api';
import { FtoReportImportPatentsApi } from './apis/import-patents-api';
import { FtoReportImportPatentsServiceImpl } from './services/impls/import-patents-service';
import { FtoReportImportPatentsService } from './services/import-patents-service';
import { FtoReportImportPatentsModelImpl } from './models/impls/import-patents-model';
import { FtoReportImportPatentsModel } from './models/import-patents-model';
import { FtoReportImportPatentsRepository } from './repositories/import-patents-repository';
import { FtoReportImportPatentsRepositoryImpl } from './repositories/impls/import-patents-repository';

// eslint-disable-next-line max-lines-per-function
export function ftoReportModule(bind: interfaces.Bind) {
    // #region: api
    bind(FtoReportApiServiceImpl).toSelf();
    bind(FtoReportCreateTaskApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportFeatureRecognizeApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportInputSummaryApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportSearchApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportResultSearchApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportTechnicalFeaturesGetApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportTechFeatureSubmitApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportTitleApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportTechEssentialUpdateApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportSubtitleInfoApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportConclusionApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportUpdateSimilarListApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportTerminateSearchAgentApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportFeatureComparisonApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportInputLanguageDetectApi).toService(FtoReportApiServiceImpl);
    bind(FtoReportImportPatentsApi).toService(FtoReportApiServiceImpl);
    // #endregion

    bind(FtoReportTaskStatusService).to(FtoReportTaskStatusServiceImpl);
    bind(FtoReportInputLanguageDetectionService).to(FtoServiceInputLanguageDetectionServiceImpl);
    bind(FtoReportTaskCreator).to(FtoReportTaskCreatorImpl);
    bind(FtoReportFeatureIdentify).to(FtoReportFeatureIdentifyImpl);
    bind(FtoReportComplexRetrievalService).to(FtoReportComplexRetrievalServiceImpl);

    bind(FtoReportImportPatentsService).to(FtoReportImportPatentsServiceImpl);
    bind(FtoReportImportPatentsModel).to(FtoReportImportPatentsModelImpl);
    bind(FtoReportImportPatentsRepository).to(FtoReportImportPatentsRepositoryImpl);
    bind(FtoReportRetrievalProgressModel).to(FtoReportRetrievalProgressModelImpl);
    bind(FtoReportRetrievalProgressService).to(FtoReportRetrievalProgressServiceImpl);
    bind(FtoReportCurrentMatchedPatentModel).to(FtoReportCurrentMatchedPatentModelImpl);
    bind(FtoReportComplexRetrievalRepository).to(FtoReportComplexRetrievalRepositoryImpl);
    bind(FtoReportSimilarRepository).to(FtoReportSimilarRepositoryImpl);
    bind(FtoReportTaskStatusModel).to(FtoReportTaskStatusModelImpl);
    bind(FtoReportRetrievalResultReportModel).to(FtoReportRetrievalResultReportModelImpl);
    bind(FtoReportAdjustTechFeaturesService).to(FtoReportAdjustTechFeaturesServiceImpl);
    bind(FtoReportNoveltyFeatureControlService).to(FtoReportNoveltyFeatureControlServiceImpl);

    bind(FtoReportRetrievalResultReportServiceImpl).toSelf();
    bind(FtoReportTechFeaturesService).toService(FtoReportRetrievalResultReportServiceImpl);
    bind(FtoReportOriginalInputService).toService(FtoReportRetrievalResultReportServiceImpl);
    bind(FtoReportTitleService).toService(FtoReportRetrievalResultReportServiceImpl);
    bind(FtoReportNoveltyReviewService).toService(FtoReportRetrievalResultReportServiceImpl);
    bind(FtoReportTechEssentialService).toService(FtoReportRetrievalResultReportServiceImpl);
    bind(FtoReportRetrievalResultReportSubtitleInfoService).toService(FtoReportRetrievalResultReportServiceImpl);
    bind(FtoReportComparativeLiteraturePublicityService).toService(FtoReportRetrievalResultReportServiceImpl);

    bind(FtoReportSimilarService).to(FtoReportSimilarServiceImpl);
    bind(FtoReportSummaryTechFeaturesService).to(FtoReportSummaryTechFeaturesServiceImpl);

    // 绑定状态类
    bind(FtoReportInitialState).toSelf();
    bind(FtoReportSummaryStartState).toSelf();
    bind(FtoReportSummaryEndState).toSelf();
    bind(FtoReportTechFeaturePullStartState).toSelf();
    bind(FtoReportTechFeaturePullSuccessState).toSelf();
    bind(FtoReportSearchFinishedState).toSelf();
    bind(FtoReportResultState).toSelf();
    bind(FtoReportSearchInputState).toSelf();

    // 绑定上下文
    bind(FtoReportContext).to(FtoReportContextImpl);

    // 绑定状态服务
    bind(FtoReportStateService).to(FtoReportStateServiceImpl);

    // 绑定命令
    bind(CommandContribution).to(FtoReportCommandContribution);
    bind(FtoReportResultRefresh).to(FtoReportResultRefreshImpl);
}
